import type {
    ExtractedDocumentPage,
    ExtractedDocumentTextItem,
} from './documentExtraction';

export interface PdfSemanticLine {
    height: number;
    items: ExtractedDocumentTextItem[];
    page: number;
    text: string;
    x: number;
    y: number;
}

export interface PdfSemanticGroup {
    kind: 'line' | 'table';
    page: number;
    rows: PdfSemanticLine[][];
}

interface PdfSemanticRow {
    height: number;
    segments: PdfSemanticLine[];
    y: number;
}

const normalizeText = (value: string) => value.replace(/\s+/g, ' ').trim();

const splitRowIntoSegments = (items: ExtractedDocumentTextItem[], page: number): PdfSemanticLine[] => {
    const sorted = [...items].sort((left, right) => left.x - right.x);
    const segments = sorted.reduce<ExtractedDocumentTextItem[][]>((result, item) => {
        const active = result.at(-1);
        const previous = active?.at(-1);
        const gap = previous ? item.x - (previous.x + previous.width) : 0;
        const threshold = previous ? Math.max(0.06, Math.max(previous.height, item.height) * 4.5) : 1;

        if (!active || gap > threshold) {
            result.push([item]);
        } else {
            active.push(item);
        }
        return result;
    }, []);

    return segments.flatMap((segment) => {
        const text = normalizeText(segment.map((item) => item.text).join(' '));

        return text
            ? [
                  {
                      height: Math.max(...segment.map((item) => item.height)),
                      items: segment,
                      page,
                      text,
                      x: Math.min(...segment.map((item) => item.x)),
                      y: Math.min(...segment.map((item) => item.y)),
                  },
              ]
            : [];
    });
};

const groupItemsIntoRows = (page: ExtractedDocumentPage): PdfSemanticRow[] => {
    const rows: ExtractedDocumentTextItem[][] = [];
    const sorted = [...page.items].sort((left, right) => left.y - right.y || left.x - right.x);

    sorted.forEach((item) => {
        const active = rows.at(-1);
        const activeY = active
            ? active.reduce((total, candidate) => total + candidate.y, 0) / active.length
            : item.y;
        const tolerance = Math.max(item.height, ...(active?.map((candidate) => candidate.height) ?? []), 0.008) * 0.7;

        if (!active || Math.abs(item.y - activeY) > tolerance) {
            rows.push([item]);
        } else {
            active.push(item);
        }
    });

    return rows.map((items) => ({
        height: Math.max(...items.map((item) => item.height)),
        segments: splitRowIntoSegments(items, page.page),
        y: Math.min(...items.map((item) => item.y)),
    }));
};

const rowsAlign = (left: PdfSemanticRow, right: PdfSemanticRow) =>
    left.segments.length === right.segments.length &&
    left.segments.every((segment, index) => Math.abs(segment.x - right.segments[index].x) <= 0.025) &&
    right.y - left.y <= Math.max(left.height, right.height) * 4.5;

const isConfidentTable = (rows: PdfSemanticRow[]) => {
    const columnCount = rows[0]?.segments.length ?? 0;
    const averageCellLength =
        rows.reduce((total, row) => total + row.segments.reduce((sum, segment) => sum + segment.text.length, 0), 0) /
        Math.max(rows.length * columnCount, 1);

    // Require repeated alignment and either a genuine multi-column grid or
    // compact two-column values. This deliberately leaves ambiguous prose as
    // independent semantic lines rather than inventing table structure.
    return rows.length >= 3 && columnCount >= 2 && (columnCount >= 3 || averageCellLength <= 40);
};

export const buildPdfSemanticGroups = (page: ExtractedDocumentPage): PdfSemanticGroup[] => {
    const rows = groupItemsIntoRows(page);
    const groups: PdfSemanticGroup[] = [];
    let index = 0;

    while (index < rows.length) {
        const candidate = [rows[index]];

        while (
            index + candidate.length < rows.length &&
            candidate[0].segments.length >= 2 &&
            rowsAlign(candidate.at(-1) ?? candidate[0], rows[index + candidate.length])
        ) {
            candidate.push(rows[index + candidate.length]);
        }

        if (isConfidentTable(candidate)) {
            groups.push({ kind: 'table', page: page.page, rows: candidate.map((row) => row.segments) });
            index += candidate.length;
            continue;
        }

        rows[index].segments.forEach((segment) => {
            groups.push({ kind: 'line', page: page.page, rows: [[segment]] });
        });
        index += 1;
    }

    return groups;
};
